import b from 'b_';
import React, { Component, Fragment } from 'react';
import { observer } from 'mobx-react';
import PropTypes from 'prop-types';
import UDPolicyEditStore from '../../stores/UDPolicies/UDPolicyEditStore';
import ConsoleVirtualList from '../ConsoleFragments/ConsoleVirtualList';
import {
    ConsoleField,
    ConsoleListAction,
} from '../ConsoleFragments';
import FuseInfoTooltip from '../FuseControls/FuseTooltip/FuseInfoTooltip';
import Icon from '../Icon/Icon';
import { LogoProofpointIcon } from '../Logo/PfptLogo';
import SearchField from '../SearchField/SearchField';
import { TBody } from '../Text/Text';

import './UserGroups.css';

const block = b.lock('UserGroups');

class UserGroups extends Component {
    static propTypes = {
        lastSyncGroupDate: PropTypes.string,
        store: UDPolicyEditStore.PropType.isRequired,
    };

    renderPfptGroup = (title, info, infoType = 'bottomRight') => <div className={block('PfptGroup')}>
        <LogoProofpointIcon className={block('PfptGroupIcon')} />
        <span className={block('PfptGroupText')}>{title}</span>
        <FuseInfoTooltip position={infoType}>{info}</FuseInfoTooltip>
    </div>;

    renderGroupTitle = (group, classMod, notFound = false) => {
        if (group.toLowerCase().startsWith('pfpt:vipusers')) {
            return this.renderPfptGroup('VIP',
                'VIP (Very Important People) group is defined in the Proofpoint User Center.');
        }

        if (group.toLowerCase().startsWith('pfpt:vapusers')) {
            return this.renderPfptGroup('VAP (Adaptive)', `
                VAP (Very Attacked People) group is dynamically
                updated daily based on data from Proofpoint threat data (TAP)`, 'bottomCenter');
        }

        return <div className={block('ListItem', classMod)}>
            <span
                className={block('ListItemText')}
                title={group}
            >{group}</span>
            {notFound ? <span className={block('ErrorLabel')}> (Not Found)</span> : null}
        </div>;
    };

    renderUnselectedGroup = ({ index }) => {
        const { unselectedUserGroups, userGroupsError, userGroups } = this.props.store;

        if (userGroupsError) {
            return <Fragment>
                <Icon type="error" className={block('ErrorIcon')} />
                <div
                    className={block('ListItem')}
                    style={{ fontWeight: 500 }}
                >Error: unable to load user groups
                </div>
            </Fragment>;
        }

        const group = unselectedUserGroups[index];
        return <Fragment>
            {this.renderGroupTitle(group, { left: true })}
            <ConsoleListAction
                className={block('RedirectUserGroup')}
                onClick={() => userGroups.push(group)}
            >{'Redirect >'}</ConsoleListAction>
        </Fragment>;
    };

    renderSelectedGroup = ({ index }) => {
        const { selectedUserGroups, allUserGroups, userGroupsError, userGroups } = this.props.store;
        const group = selectedUserGroups[index];
        const notFound = !userGroupsError && allUserGroups.indexOf(group) === -1;
        return <Fragment>
            {userGroupsError ? null : <ConsoleListAction
                className={block('RemoveUserGroup')}
                onClick={() => userGroups.remove(group)}
            >{'< Remove'}</ConsoleListAction>}
            {notFound ? <Icon type="error" className={block('ErrorIcon')} /> : null}
            {this.renderGroupTitle(group, { right: true }, notFound)}
        </Fragment>;
    };

    render() {
        const { lastSyncGroupDate, store } = this.props;
        const {
            userGroupFilterStore,
            unselectedUserGroups,
            selectedUserGroups,
            userGroupsError,
        } = store;

        return <Fragment>
            <ConsoleField
                name="Select user groups to redirect to Isolation"
                info="Choose specific user groups, defined in Proofpoint Protection,
                      to redirect when a URL is clicked in corporate email."
            >
                <SearchField
                    className={block('FilterInput')}
                    inputStore={userGroupFilterStore}
                    placeholder="Search by Group Name or Email"
                />
            </ConsoleField>
            <div className={block('Row')}>
                <div className={block('Column')}>
                    <ConsoleVirtualList
                        className={block('List')}
                        title={<div className={block('AvailableGroupsTitle')}>
                            Available Groups ({userGroupsError ? 0 : unselectedUserGroups.length})
                            {lastSyncGroupDate ? <TBody className={block('UDLastSyncGroup')}>
                                <span>Last Synchronization:</span>
                                <span>{lastSyncGroupDate}</span>
                            </TBody> : null}
                        </div>}
                        itemCount={userGroupsError ? 1 : unselectedUserGroups.length}
                        itemSize={48}
                        height={383}
                    >{this.renderUnselectedGroup}</ConsoleVirtualList>
                </div>
                <div className={block('Column')}>
                    <ConsoleVirtualList
                        className={block('List')}
                        title={`Groups to Redirect (${selectedUserGroups.length})`}
                        itemCount={selectedUserGroups.length}
                        itemSize={48}
                        height={383}
                    >{this.renderSelectedGroup}</ConsoleVirtualList>
                </div>
            </div>
        </Fragment>;
    }
}

export default observer(UserGroups);
